import { useMemo } from 'react';
import { css } from '@emotion/react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { PersonOrderBy_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import { DataSourcesHooks } from '@frontend/api-data-sources';
import { PersonsV3Combined } from '@frontend/api-person';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  ComboboxBase,
  PopoverMenuItem,
  SpinningLoader,
  Text,
  TextField,
  UserIcon,
  useDebouncedValue,
  useFormField,
  usePopoverMenu,
  useTooltip,
} from '@frontend/design-system';
import { PaymentsPersonSearchContent } from './payments-person-search-content';

const styles = {
  popoverMenuItem: css`
    height: auto;
    padding: ${theme.spacing(1, 2)};
    min-height: auto;
  `,
  noResult: css`
    padding: ${theme.spacing(1, 2)};
  `,
  textField: (isOpen: boolean) => css`
    border-radius: ${isOpen ? '8px 8px 0 0' : theme.borderRadius.small};
  `,
};

const API_RESPONSE_LIMIT = 50;
export const PatientDropdown = ({
  setSelectedPatient,
  locationId,
  trackingId,
  searchFieldProps,
  disabled = false,
  disabledMessage,
}: {
  setSelectedPatient: (patient: Person | null) => void;
  locationId: string | undefined;
  trackingId?: string;
  searchFieldProps: ReturnType<typeof useFormField<'text'>>;
  disabled?: boolean;
  disabledMessage: string;
}) => {
  const { t } = useTranslation('payments');
  const { demoSourceIds } = DataSourcesHooks.useDemoLocationSourceIdsShallowStore('demoSourceIds');
  const { onChange: searchFieldOnChange, ...searchField } = searchFieldProps;

  const { Tooltip, tooltipProps, triggerProps: tooltipTriggerProps } = useTooltip();
  const { getMenuProps, getItemProps, getTriggerProps, open, close, activeIndex, setActiveIndex, refs, isOpen } =
    usePopoverMenu<HTMLInputElement>({
      placement: 'bottom',
      interactionOptions: { listNavigation: { virtual: true }, typeahead: { enabled: false } },
    });

  const searchValue = useDebouncedValue(searchField.value, API_RESPONSE_LIMIT);

  const personsQuery = PersonsV3Combined.useConvolutedInsanityCombinedPersonV3Search({
    search: searchValue,
    locationIds: locationId ? [locationId] : [],
    sourceIds: demoSourceIds,
    page: {
      size: API_RESPONSE_LIMIT,
      order: [
        { orderBy: PersonOrderBy_Enum.LAST_NAME, descending: false },
        { orderBy: PersonOrderBy_Enum.FIRST_NAME, descending: false },
      ],
    },
  });

  const handleSelectPerson = (person: Person) => {
    setSelectedPatient(person);
    close();
  };
  const getDropdownWidth = () => {
    const dropdown = document.querySelector(`#${searchFieldProps.id}`);
    const width = dropdown?.clientWidth;
    return Number(width) + 16 + 16 + 16 + 12;
  };
  const content = useMemo(() => {
    if (personsQuery.isLoading) {
      return <SpinningLoader css={{ margin: `${theme.spacing(1)} auto` }} />;
    } else if (personsQuery.persons.length) {
      return personsQuery.persons.map((person, index) => (
        <PopoverMenuItem
          {...getItemProps({
            index,
            onClick: () => handleSelectPerson(person),
          })}
          key={person.personId}
          css={styles.popoverMenuItem}
        >
          <PaymentsPersonSearchContent person={person} />
        </PopoverMenuItem>
      ));
    } else if (searchField.value === '' || personsQuery.persons.length === 0) {
      return (
        <Text size='small' css={styles.noResult}>
          {t('No results to display...')}
        </Text>
      );
    }
    return;
  }, [personsQuery]);

  const { ref, ...triggerProps } = getTriggerProps({
    onKeyDown: (event) => {
      if (event.key === 'Enter' && activeIndex != null && personsQuery.persons[activeIndex]) {
        const item = personsQuery.persons[activeIndex];

        searchFieldOnChange({ name: 'person-search', value: `${item.firstName} ${item.lastName}` });
        setSelectedPatient(item);
        setActiveIndex(null);
        close();
      }
    },
    onChange: (e) => {
      searchFieldOnChange(e);
      // @ts-ignore our change events are still messed up :(
      if (e.target.value) {
        open();
      } else {
        close();
        setSelectedPatient(null);
      }
    },
  });

  return (
    <>
      <ComboboxBase
        Input={
          <TextField
            {...tooltipTriggerProps}
            {...triggerProps}
            data-trackingid={trackingId}
            name='person-search'
            label={t('Customer *')}
            fieldComponentProps={{ ref: refs.setReference, autoComplete: 'off' }}
            startAdornment={<UserIcon size={16} color='light' />}
            css={styles.textField(isOpen)}
            {...searchFieldProps}
            disabled={disabled || searchField.disabled}
          />
        }
        menuProps={getMenuProps()}
        menuStyles={css({
          maxWidth: 600,
          minWidth: 228,
          width: getDropdownWidth(),
          marginLeft: '-12px',
          marginTop: theme.spacing(0.4),
          borderRadius: '0 0 4px 4px',
        })}
      >
        {content}
      </ComboboxBase>
      {disabled && disabledMessage && <Tooltip {...tooltipProps}>{disabledMessage}</Tooltip>}
    </>
  );
};
